<template lang="pug">
div.home-root
  tab-menu
  div.home-container(v-loading="loading")
    div.home-header
      h1.home-title Bienvenido, {{ userName }}
      p.home-subtitle Resumen rápido de los procesos automáticos del día ({{ formattedDate }})

    el-card.summary-card(shadow="hover")
      div.summary-label Resumen del día
      div.summary-stats
        div.stat.stat-total
          div.stat-value {{ summary.total }}
          div.stat-label Total
        div.stat.stat-ok
          div.stat-value {{ summary.ok }}
          div.stat-label Exitosos
        div.stat.stat-error
          div.stat-value {{ summary.error }}
          div.stat-label Con error

    div.category-grid
      el-card.category-card(
        v-for="category in visibleCategories"
        :key="category.key"
        shadow="hover"
      )
        el-collapse.category-collapse(v-model="openCategories[category.key]")
          el-collapse-item(:name="category.key")
            template(slot="title")
              div.category-header
                span.category-label {{ category.label }}
                div.category-badges
                  el-tag(size="mini" type="info") {{ category.count }} procesos
                  el-tag(v-if="category.ok > 0" size="mini" type="success") ✓ {{ category.ok }}
                  el-tag(v-if="category.error > 0" size="mini" type="danger") ⚠ {{ category.error }}
            div.category-events(v-if="category.events.length")
              div.event(v-for="event in category.events" :key="event.date + event.detail")
                div.event-row
                  span.event-status(:class="statusClass(event.status)")
                    | {{ event.status === 'error' ? '✕' : '✓' }}
                  span.event-time {{ shortTime(event.date) }}
                  span.event-detail {{ baseLabel(event.detail) }}
            div.category-empty(v-else)
              | Sin procesos hoy en esta categoría
</template>

<script>
import TabMenu from '@/components/layout/TabMenu.vue';
// eslint-disable-next-line import/no-cycle
import apiService from '@/services/apiService';
import store from '@/store';

const ROLES_WITHOUT_REIMBURSEMENTS = ['observer'];
const REFRESH_INTERVAL_MS = 60 * 60 * 1000;

export default {
  name: 'Home',
  components: { TabMenu },
  data() {
    return {
      loading: true,
      categories: [],
      openCategories: {},
      dashboardDate: new Date(),
      refreshTimer: null,
    };
  },
  computed: {
    userName() {
      return store.state.user.name || 'Usuario';
    },
    userRole() {
      return store.state.user.role || '';
    },
    formattedDate() {
      return this.dashboardDate.toLocaleDateString('es-CL', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
      });
    },
    visibleCategories() {
      const hideReimbursements = ROLES_WITHOUT_REIMBURSEMENTS.includes(this.userRole);
      return this.categories.filter((c) => !(hideReimbursements && c.key === 'reimbursements'));
    },
    // El resumen se calcula sobre las categorías visibles, para que cuadre con lo que ve cada rol.
    summary() {
      return this.visibleCategories.reduce((acc, category) => ({
        total: acc.total + category.count,
        ok: acc.ok + category.ok,
        error: acc.error + category.error,
      }), { total: 0, ok: 0, error: 0 });
    },
  },
  methods: {
    async loadData({ silent = false } = {}) {
      this.loading = !silent;
      try {
        const response = await apiService.get_activities_dashboard();
        const { data } = response.body;
        this.categories = data.categories;
        this.dashboardDate = this.parseDate(data.date);
        if (!silent) this.openCategories = this.initialOpenState(data.categories);
      } catch (e) {
        if (!silent) this.$message.error('No se pudo cargar el dashboard.');
      } finally {
        this.loading = false;
      }
    },
    initialOpenState(categories) {
      return categories.reduce((state, category) => ({
        ...state,
        [category.key]: category.error > 0 ? [category.key] : [],
      }), {});
    },
    parseDate(raw) {
      if (!raw) return new Date();
      const [year, month, day] = raw.split('-').map(Number);
      return new Date(year, month - 1, day);
    },
    shortTime(rawDate) {
      if (!rawDate) return '';
      const parts = rawDate.split(' ');
      if (parts.length < 2) return rawDate;
      return parts[1].slice(0, 5);
    },
    baseLabel(detail) {
      if (!detail) return '';
      return detail.split(' — Error:')[0];
    },
    statusClass(status) {
      return status === 'error' ? 'event-status-error' : 'event-status-ok';
    },
  },
  mounted() {
    this.loadData();
    this.refreshTimer = setInterval(() => this.loadData({ silent: true }), REFRESH_INTERVAL_MS);
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer);
  },
};
</script>

<style scoped>
.home-root {
  width: 100%;
}

.home-container {
  width: 100%;
  box-sizing: border-box;
  padding: 24px 32px;
  background: #f5f7fa;
  min-height: calc(100vh - 80px);
  overflow-x: auto;
}

.category-grid {
  min-width: 640px;
}

@media (max-width: 640px) {
  .home-container {
    padding: 16px;
  }
}

.home-header {
  margin-bottom: 24px;
}

.home-title {
  font-size: 26px;
  margin: 0 0 6px 0;
  color: #1f2937;
  font-weight: 600;
}

.home-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  text-transform: capitalize;
}

.summary-card,
.category-card {
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.summary-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

@media (max-width: 700px) {
  .summary-stats {
    grid-template-columns: minmax(0, 1fr);
  }
}

.stat {
  background: #fafbfc;
  border-radius: 8px;
  padding: 18px 20px;
  border-left: 4px solid;
}

.stat-total {
  border-left-color: #3b82f6;
}

.stat-ok {
  border-left-color: #10b981;
}

.stat-error {
  border-left-color: #ef4444;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.1;
  color: #1f2937;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 13px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.category-grid {
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  align-items: start;
}

@media (max-width: 1100px) {
  .category-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.category-collapse,
.category-collapse ::v-deep .el-collapse-item__wrap {
  border: none;
}

.category-collapse ::v-deep .el-collapse-item__header {
  height: auto;
  line-height: normal;
  border-bottom: none;
}

.category-collapse ::v-deep .el-collapse-item__header > .category-header {
  flex: 1;
  min-width: 0;
}

.category-collapse ::v-deep .el-collapse-item__content {
  padding-bottom: 0;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-right: 8px;
}

.category-label {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  min-width: 0;
}

.category-badges {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.category-events {
  max-height: 220px;
  overflow-y: auto;
  padding-right: 4px;
}

.category-events::-webkit-scrollbar {
  width: 6px;
}

.category-events::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.category-events::-webkit-scrollbar-track {
  background: transparent;
}

.event {
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
}

.event:last-child {
  border-bottom: none;
}

.event-row {
  display: grid;
  grid-template-columns: 22px 60px 1fr;
  align-items: center;
  gap: 8px;
}

.event-status {
  font-weight: 700;
  font-size: 14px;
  text-align: center;
}

.event-status-ok {
  color: #10b981;
}

.event-status-error {
  color: #ef4444;
}

.event-time {
  font-size: 13px;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}

.event-detail {
  font-size: 14px;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-empty {
  font-size: 13px;
  color: #9ca3af;
  font-style: italic;
  padding: 12px 0;
}
</style>
